"use client"

import localFont from "next/font/local"
import { useEffect, useRef, useState, type FormEvent } from "react"
import { ArrowDown01Icon, Globe02Icon } from "hugeicons-react"
import { Button } from "@quibas/integration-hub-ui/button"
import { Input } from "@quibas/integration-hub-ui/input"
import { Card, CardContent, CardFooter, CardHeader } from "@quibas/integration-hub-ui/card"
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@quibas/integration-hub-ui/dropdown-menu"
import { LoaderIcon, TriangleAlert } from "@quibas/integration-hub-ui/icons"
import { Separator } from "@quibas/integration-hub-ui/separator"
import { cn } from "@quibas/integration-hub-ui/lib/utils"
import { LoginBackground } from "./login-background"
import { useLocale } from "@/i18n/provider"
import { loginCopy } from "./login-copy"
import { LoginTurnstileError, useLoginTurnstile } from "./use-login-turnstile"
import styles from "./login.module.css"

// Same Tumbly wordmark as the Procurement sign-in; the font is loaded only for this screen.
const brandFont = localFont({ src: "../../app/fonts/TumblyBold.otf", weight: "700", display: "swap" })

const AUTH_START_PATH = "/api/auth/external/start"
const REDIRECT_WAIT_MS = 15_000
const EMAIL_PATTERN = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.[A-Za-z]{2,63}$/

/** Sign-in capacity or attempt limits (429): the account was not checked, so retry shortly. */
class LoginBusyError extends Error {}

export function LoginScreen({ enabled, failed = false, failure, currentYear, turnstileSiteKey = "", nextPath = "" }: {
  enabled: boolean
  failed?: boolean
  failure?: "access_denied" | "login_unavailable" | "logout_incomplete"
  currentYear: number
  nextPath?: string
  turnstileSiteKey?: string
}) {
  const { locale, setLocale } = useLocale()
  const [phase, setPhase] = useState<"idle" | "submitting" | "delayed" | "rejected" | "challenge-rejected" | "busy">("idle")
  const pendingRequest = useRef<AbortController | null>(null)
  const submissionPending = useRef(false)
  const feedbackRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const emailFieldRef = useRef<HTMLDivElement>(null)
  const [emailError, setEmailError] = useState<"emailRequired" | "emailInvalid" | null>(null)
  const [failureDismissed, setFailureDismissed] = useState(false)
  const { challengeContainerRef, isChallengeVisible, getToken, reset: resetChallenge } = useLoginTurnstile({ enabled, siteKey: turnstileSiteKey, locale,
    callbacks: { isAttemptActive: () => submissionPending.current } })
  const t = loginCopy[locale]
  const submitting = phase === "submitting"
  const error = phase === "challenge-rejected" ? t.challengeFailure : phase === "delayed" ? t.delayed : phase === "rejected" ? t.rejected :
    phase === "busy" ? t.busy : !failureDismissed ? failure ? t[failure] : failed ? t.failed : null : null

  useEffect(() => {
    if (failed || failure || phase === "delayed" || phase === "rejected" || phase === "challenge-rejected" || phase === "busy") {
      feedbackRef.current?.focus({ preventScroll: true })
      replayShake(emailFieldRef.current)
    }
  }, [failed, failure, phase])

  useEffect(() => {
    if (!submitting || isChallengeVisible) return
    const timeout = window.setTimeout(() => {
      pendingRequest.current?.abort()
      pendingRequest.current = null
      submissionPending.current = false
      resetChallenge()
      setPhase("delayed")
    }, REDIRECT_WAIT_MS)
    return () => window.clearTimeout(timeout)
  }, [submitting, resetChallenge, isChallengeVisible])

  useEffect(() => () => { pendingRequest.current?.abort(); pendingRequest.current = null }, [])

  useEffect(() => {
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      submissionPending.current = false
      setPhase("idle")
    }
    window.addEventListener("pageshow", restore)
    return () => window.removeEventListener("pageshow", restore)
  }, [])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!enabled || submissionPending.current) {
      event.preventDefault()
      return
    }
    const input = emailRef.current
    const email = input?.value.trim() ?? ""
    if (!email || email.length > 254 || !EMAIL_PATTERN.test(email)) {
      event.preventDefault()
      setEmailError(email ? "emailInvalid" : "emailRequired")
      input?.focus({ preventScroll: true })
      replayShake(emailFieldRef.current)
      return
    }
    if (input) input.value = email
    setEmailError(null)
    submissionPending.current = true
    setPhase("submitting")
    const controller = new AbortController()
    pendingRequest.current = controller
    try {
      const turnstileToken = await getToken()
      if (controller.signal.aborted) return
      const response = await fetch(AUTH_START_PATH, {
        method: "POST", headers: { Accept: "application/json" }, body: new URLSearchParams({ email, turnstile_token: turnstileToken, next: nextPath }),
        credentials: "same-origin", mode: "same-origin", cache: "no-store", redirect: "error", signal: controller.signal,
      })
      if (response.status === 429) {
        void response.body?.cancel().catch(() => {})
        throw new LoginBusyError()
      }
      if (!response.ok || !response.headers.get("content-type")?.startsWith("application/json") || !response.body) throw new Error()
      const reader = response.body.getReader()
      let size = 0
      let text = ""
      const decoder = new TextDecoder("utf-8", { fatal: true })
      try {
        while (true) {
          const chunk = await reader.read()
          if (chunk.done) break
          size += chunk.value.byteLength
          if (size > 4096) throw new Error()
          text += decoder.decode(chunk.value, { stream: true })
        }
        text += decoder.decode()
      } finally { void reader.cancel().catch(() => {}) }
      const payload: unknown = JSON.parse(text)
      if (!payload || typeof payload !== "object" || !("authorizationUrl" in payload) || typeof payload.authorizationUrl !== "string") throw new Error()
      const destination = new URL(payload.authorizationUrl)
      if (destination.protocol !== "https:" || destination.username || destination.password || destination.hash ||
          !/^\/realms\/[^/]+\/protocol\/openid-connect\/auth$/.test(destination.pathname)) throw new Error()
      if (pendingRequest.current !== controller || controller.signal.aborted) return
      // Navigate only to the authorization URL produced by the app-local trusted
      // runtime. Never follow a failed POST or expose its response body as an error.
      window.location.assign(destination.href)
    } catch (error) {
      if (pendingRequest.current !== controller) return
      pendingRequest.current = null
      submissionPending.current = false
      resetChallenge()
      setPhase(error instanceof LoginTurnstileError ? "challenge-rejected" : error instanceof LoginBusyError ? "busy" : "rejected")
    }
  }

  return (
    <main lang={locale} className={cn(styles.shell, "relative isolate min-h-dvh overflow-x-hidden lg:h-dvh lg:min-h-0 lg:overflow-hidden")}>
      <LoginBackground />
      <div className="relative z-10 grid min-h-dvh w-full grid-cols-1 grid-rows-[auto_1fr] lg:h-full lg:min-h-0 lg:grid-cols-[minmax(0,1.16fr)_minmax(36rem,0.84fr)] lg:grid-rows-1">
        <section aria-label="Quibas Integration Hub" className={cn(styles.brand, "@container relative order-1 flex h-40 min-h-40 overflow-hidden sm:h-48 sm:min-h-48 lg:h-full lg:min-h-0")}>
          {/* Procurement-style lockup: wordmark, divider and product name; narrow columns stack the name. */}
          <div className="pointer-events-none absolute top-6 left-6 flex items-center gap-[0.33em] text-4xl @max-xl:flex-col @max-xl:items-start @max-xl:gap-[0.1em] sm:top-8 sm:right-10 sm:left-10 lg:top-[clamp(2.5rem,5.5dvh,4rem)] lg:right-[clamp(2rem,4cqw,4rem)] lg:left-[clamp(2.5rem,3.8vw,4rem)] lg:text-5xl">
            <span aria-hidden="true" className={cn(brandFont.className, "block shrink-0 leading-none whitespace-nowrap select-none")}>quibas</span>
            <span aria-hidden="true" className="hidden h-[0.78em] w-px shrink-0 bg-current opacity-20 sm:@xl:block" />
            <span className="hidden text-[0.39em] font-medium leading-tight text-balance opacity-70 sm:inline">Integration Hub</span>
          </div>
          <ul className="pointer-events-none absolute top-1/2 right-[clamp(2rem,4cqw,4rem)] left-[clamp(2.5rem,6.5cqw,7.5rem)] hidden -translate-y-1/2 list-none flex-col gap-[0.35em] text-[clamp(1.75rem,min(6.4cqw,7dvh),5.25rem)] leading-[0.96] tracking-[-0.045em] text-balance lg:flex">
            <li className="whitespace-nowrap text-[0.92em] font-medium opacity-70">{t.slogans[0]}</li>
            <li className="whitespace-nowrap text-[1em] font-medium opacity-85">{t.slogans[1]}</li>
            <li className="whitespace-nowrap text-[1.08em] font-semibold">{t.slogans[2]}</li>
          </ul>
        </section>

        <section aria-labelledby="login-title" className="relative z-10 order-2 flex min-h-[calc(100dvh-10rem)] items-start justify-center px-4 py-5 text-foreground sm:min-h-[calc(100dvh-12rem)] sm:px-8 sm:py-8 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:px-[clamp(2rem,4vw,4.5rem)] lg:py-[clamp(2rem,5dvh,4rem)]">
          <Card className="my-auto w-full max-w-[37.5rem] shrink-0 gap-0 rounded-[1.15rem] border-border py-0 shadow-2xl lg:min-h-[min(40rem,calc(100dvh-4rem))]">
            <CardHeader className="flex flex-row items-center justify-end px-6 pt-6 sm:px-8 sm:pt-8 lg:px-10 lg:pt-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" type="button" aria-label={t.language} className="h-10 gap-2 border border-transparent bg-transparent px-3.5 shadow-none hover:bg-accent" disabled={submitting}>
                    <Globe02Icon aria-hidden="true" className="size-[1.2rem]" />
                    <span aria-hidden="true">{locale.toUpperCase()}</span>
                    <ArrowDown01Icon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" aria-label={t.language} className="w-64 max-w-[calc(100vw-2rem)] p-3">
                  <DropdownMenuRadioGroup value={locale} onValueChange={value => { if (value === "tr" || value === "en") setLocale(value) }}>
                    <DropdownMenuRadioItem value="tr" lang="tr">Türkçe</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="en" lang="en">English</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>

            <CardContent className="flex flex-1 items-center px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
              <div className="mx-auto w-full max-w-[28rem]">
                <div className="mb-8">
                  <h1 id="login-title" className="text-4xl font-semibold tracking-tight text-balance">{t.title}</h1>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">{t.description}</p>
                </div>

                <form action={AUTH_START_PATH} method="post" noValidate onSubmit={submit} aria-busy={submitting} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div ref={emailFieldRef} className="flex flex-col gap-1.5">
                      <label htmlFor="email" className={cn("text-sm font-medium", emailError && !submitting && "text-destructive")}>{locale === "tr" ? "E-posta adresi" : "Email address"}</label>
                      <Input ref={emailRef} id="email" name="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false}
                        required maxLength={254} placeholder="name@company.com" disabled={!enabled} readOnly={submitting}
                        onChange={() => {
                          setEmailError(null)
                          setFailureDismissed(true)
                          if (!submissionPending.current) setPhase("idle")
                          emailFieldRef.current?.classList.remove(styles.shake)
                        }}
                        aria-invalid={emailError ? true : undefined} aria-errormessage={emailError ? "email-error" : undefined}
                        aria-describedby={cn(emailError ? "email-error" : error && !submitting && "login-feedback", !enabled && "login-status") || undefined} className="h-11 rounded-lg px-3.5 text-sm shadow-none" />
                    </div>
                    {error && !submitting && !emailError ? (
                      <div ref={feedbackRef} id="login-feedback" role="alert" tabIndex={-1} className="flex min-h-5 items-start gap-2 rounded-md text-xs leading-5 text-destructive outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                        <p>{error}</p>
                      </div>
                    ) : (
                    <p id="email-error" role={emailError ? "alert" : undefined} aria-live="polite" aria-atomic="true" aria-hidden={!emailError}
                      className={cn("flex min-h-5 items-start gap-2 overflow-hidden text-xs leading-5 text-destructive", !emailError && "invisible")}>
                      <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                      <span>{emailError ? t[emailError] : "\u00a0"}</span>
                    </p>
                    )}
                  </div>
                  {!enabled && (
                    <div id="login-status" className="rounded-lg border border-border bg-background px-3.5 py-3 text-sm">
                      <p className="font-medium">{t.unavailableTitle}</p>
                      <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{t.unavailableDescription}</p>
                    </div>
                  )}
                  <div ref={challengeContainerRef} tabIndex={-1}
                    aria-label={t.securityCheck} className={isChallengeVisible ? "w-full" : "sr-only"} />
                  <Button type="submit" size="lg" className="h-11 w-full rounded-lg text-sm font-medium" disabled={!enabled || submitting} aria-describedby={cn(!enabled && "login-status", error && !submitting && "login-feedback") || undefined}>
                    {submitting && <LoaderIcon aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />}
                    {submitting ? t.submitting : enabled ? t.submit : t.unavailableTitle}
                  </Button>
                  <p role="status" aria-live="polite" className="sr-only">{submitting ? t.submitting : ""}</p>
                </form>

              </div>
            </CardContent>

            <CardFooter className="mt-auto block px-6 pt-0 pb-6 sm:px-8 sm:pb-8 lg:px-10 lg:pb-8">
              <footer className="w-full text-muted-foreground">
                <Separator className="mb-4" />
                <div className="flex min-w-0 flex-wrap items-center justify-end gap-3 text-[0.55rem] tracking-tight sm:text-xs sm:tracking-normal">
                  <p>Quibas Integration Hub © {currentYear}</p>
                </div>
              </footer>
            </CardFooter>
          </Card>
        </section>
      </div>
    </main>
  )
}

function replayShake(element: HTMLElement | null) {
  if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
  element.classList.remove(styles.shake)
  // Restart even on consecutive invalid submissions without remounting the input.
  void element.offsetWidth
  element.classList.add(styles.shake)
}
